/**
 * bom-tree.utils.ts - Utility pro práci se stromem kusovníku (Bill of Materials)
 * 
 * FUNKCE:
 * - Sestavení stromu kusovníku z produktů
 * - Procházení stromu a zjišťování závislostí
 * - Detekce cyklických závislostí
 * - Získání hloubky procesu
 */

import { ProductModel } from '@/libs/Api';

/**
 * Reprezentace uzlu ve stromu kusovníku
 */
export interface BomNode {
    productId: string;
    product: ProductModel;
    depth: number;
    children: BomNode[];
}

/**
 * Sestaví strom kusovníku pro daný výstupní produkt.
 * 
 * Strom se buduje rekurzivně od výstupního produktu k jeho komponentám (inputProducts).
 * Depth 0 = výstupní produkt (root), vyšší depth = komponenty níže v hierarchii.
 * 
 * @param outputProductId ID konečného produktu
 * @param allProducts Všechny produkty z databáze
 * @param visited Set navštívených produktů (ochrana před cykly)
 * @param depth Aktuální hloubka v stromu
 * @returns BomNode nebo null pokud produkt není nalezen nebo je detekován cyklus
 */
export function buildBomTree(
    outputProductId: string,
    allProducts: ProductModel[],
    visited: Set<string> = new Set(),
    depth = 0
): BomNode | null {
    // Ochrana proti cyklům
    if (visited.has(outputProductId)) {
        console.warn(`[BOM] Cycle detected for product ${outputProductId}`);
        return null;
    }
    
    const product = allProducts.find(p => p.id === outputProductId);
    if (!product) {
        console.warn(`[BOM] Product not found: ${outputProductId}`);
        return null;
    }
    
    visited.add(outputProductId);
    
    const node: BomNode = {
        productId: outputProductId,
        product,
        depth,
        children: []
    };
    
    // Rekurzivně projít komponenty (inputProducts z billOfMaterialItems)
    const bomItems = product.billOfMaterialItems || [];
    for (const item of bomItems) {
        if (!item.inputProductId) continue;
        
        // Vytvoříme novou kopii visited pro každou větev, aby cykly v různých větvích neovlivnily další
        const childNode = buildBomTree(
            item.inputProductId,
            allProducts,
            new Set(visited), // nová kopie pro každou větev
            depth + 1
        );
        
        if (childNode) {
            node.children.push(childNode);
        }
    }
    
    return node;
}

/**
 * Získá všechny unikátní produkty ze stromu kusovníku.
 * Používá se pro zjištění, které produkty potřebujeme načíst z databáze.
 * 
 * @param node Root uzel stromu
 * @returns Pole unikátních product IDs
 */
export function getUniqueProductIds(node: BomNode): string[] {
    const ids = new Set<string>();
    
    function traverse(n: BomNode) {
        ids.add(n.productId);
        for (const child of n.children) {
            traverse(child);
        }
    }
    
    traverse(node);
    return Array.from(ids);
}

/**
 * Zploští strom kusovníku do lineárního seznamu se zachováním depth.
 * Používá se pro iteraci přes všechny produkty v procesu.
 * 
 * @param node Root uzel stromu
 * @returns Pole objektů { productId, depth }
 */
export function flattenBomTree(node: BomNode): Array<{ productId: string; depth: number }> {
    const result: Array<{ productId: string; depth: number }> = [];
    
    function traverse(n: BomNode) {
        result.push({ productId: n.productId, depth: n.depth });
        for (const child of n.children) {
            traverse(child);
        }
    }
    
    traverse(node);
    return result;
}

/**
 * Najde maximální depth ve stromu (vstupní produkt procesu).
 * Produkt s maximální depth je ten, který je na začátku procesu.
 * 
 * @param node Root uzel stromu
 * @returns Maximální depth nalezený ve stromu
 */
export function getMaxDepth(node: BomNode): number {
    let maxDepth = node.depth;
    
    for (const child of node.children) {
        const childMax = getMaxDepth(child);
        if (childMax > maxDepth) {
            maxDepth = childMax;
        }
    }
    
    return maxDepth;
}

/**
 * Získá všechny listy stromu (produkty bez dalších komponent).
 * Listy jsou nejčastěji vstupní produkty/materiály procesu.
 * 
 * @param node Root uzel stromu
 * @returns Pole listových uzlů
 */
export function getLeafNodes(node: BomNode): BomNode[] {
    const leaves: BomNode[] = [];
    
    function traverse(n: BomNode) {
        if (n.children.length === 0) {
            leaves.push(n);
        } else {
            for (const child of n.children) {
                traverse(child);
            }
        }
    }
    
    traverse(node);
    return leaves;
}

/**
 * Spočítá celkový počet uzlů ve stromu.
 * 
 * @param node Root uzel stromu
 * @returns Celkový počet produktů v procesu
 */
export function countNodes(node: BomNode): number {
    let count = 1; // root
    
    for (const child of node.children) {
        count += countNodes(child);
    }
    
    return count;
}

/**
 * Najde všechny produkty na dané úrovni (depth).
 * 
 * @param node Root uzel stromu
 * @param targetDepth Cílová hloubka
 * @returns Pole uzlů na dané úrovni
 */
export function getNodesAtDepth(node: BomNode, targetDepth: number): BomNode[] {
    const nodes: BomNode[] = [];
    
    function traverse(n: BomNode) {
        if (n.depth === targetDepth) {
            nodes.push(n);
        }
        for (const child of n.children) {
            traverse(child);
        }
    }
    
    traverse(node);
    return nodes;
}
